import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default permissions for reference
const DEFAULT_PERMISSIONS = {
  modules: {
    dashboard: true,
    pos: true,
    support: true,
    credits: true,
    sales_history: true,
    returns: true,
    products: true,
    categories: true,
    customers: true,
    quotations: true,
    inventory_scan: true,
    equipment_lookup: true,
    suppliers: false,
    purchases: true,
    preparation: true,
    lost_damaged: true,
    inventory_aging: true,
    reports: false,
    analytics: false,
    invoice_settings: false,
    shop_config: false,
    audit_log: false,
    settings: false,
    users: false
  },
  actions: {
    delete_products: false,
    delete_categories: false,
    delete_customers: false,
    delete_invoices: false,
    delete_quotations: false,
    cancel_invoices: false,
    adjust_inventory: true,
    create_users: false
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [branch, setBranchState] = useState(() => {
    try {
      const raw = localStorage.getItem('branch');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Keep X-Branch-Id header in sync with selected branch
  useEffect(() => {
    if (branch?.id) {
      axios.defaults.headers.common['X-Branch-Id'] = branch.id;
    } else {
      delete axios.defaults.headers.common['X-Branch-Id'];
    }
  }, [branch]);

  const setBranch = (b) => {
    if (b) {
      localStorage.setItem('branch', JSON.stringify(b));
    } else {
      localStorage.removeItem('branch');
    }
    setBranchState(b);
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (pin) => {
    const response = await axios.post(`${API}/auth/login`, { pin });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, pin, role = 'vendedor', permissions = null, email = null) => {
    const payload = { name, pin, role };
    if (email) payload.email = email;
    if (permissions) {
      payload.permissions = permissions;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('branch');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Branch-Id'];
    setToken(null);
    setUser(null);
    setBranchState(null);
  };

  const isAdmin = user?.role === 'admin';

  // Check if user has access to a module
  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const permissions = user.permissions || DEFAULT_PERMISSIONS;
    return permissions?.modules?.[module] ?? false;
  };

  // Check if user can perform an action
  const hasActionPermission = (action) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const permissions = user.permissions || DEFAULT_PERMISSIONS;
    return permissions?.actions?.[action] ?? false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      token, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin,
      hasModuleAccess,
      hasActionPermission,
      branch,
      setBranch
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
